import { isInverseRelation, isOwningRelation, isRelation } from '@contember/schema-utils';
import { formatEntityName, formatFieldName } from '../../../common';
import { isAutoGeneratedColumnName } from '../../builder/utils';
import { createSchemaStatementHandler } from '../../createSchemaStatementHandler';
import { SchemaDslBuilderError } from '../../SchemaDslBuilder';
export const translatableRelationStmtInstruction = `
#### Set translatable relationship
- Syntax: \`translatable relation <owning_entity>.<owning_field>;\`
- Example: \`translatable relation Book.genres;\`
`;
export const stmtSetTranslatableRelation = ({ entityName, fieldName }) => {
    return `translatable relation ${entityName}.${fieldName};`;
};
export const stmtForHumanSetTranslatableRelation = ({ entityName, fieldName }) => {
    return `Set the relationship between the database tables __${entityName}__ and __${fieldName}__ as translatable.`;
};
export const setTranslatableRelationHandler = createSchemaStatementHandler({
    type: 'translatable field',
    parse: line => {
        const match = /^translatable (relation|column|field) (?<entityName>\w+)\.(?<fieldName>\w+)/.exec(line);
        return match?.groups
            ? {
                type: 'translatable field',
                entityName: formatEntityName(match.groups.entityName),
                fieldName: formatFieldName(match.groups.fieldName),
            }
            : null;
    },
    getHumanStmt: (stmt) => stmtForHumanSetTranslatableRelation(stmt),
    apply: (stmt, builder) => {
        builder.updateSchemaExtensions(it => {
            const entity = builder.schema.model.entities[stmt.entityName] || {};
            const isPluginEntity = Object.values(it.plugins).some(plugin => Object.keys(plugin.schema?.model.entities ?? {})?.includes(stmt.entityName));
            if (!entity) {
                throw new Error(`Entity ${stmt.entityName} not found.`);
            }
            if (isPluginEntity) {
                throw new SchemaDslBuilderError(`Entity ${stmt.entityName} is a plugin entity and cannot be marked as translatable. Plugins do not support translatable fields.`);
            }
            const field = entity.fields[stmt.fieldName];
            if (!field) {
                throw new SchemaDslBuilderError(`Field ${stmt.fieldName} not found in entity ${stmt.entityName}.`);
            }
            if (isAutoGeneratedColumnName(stmt.fieldName)) {
                throw new SchemaDslBuilderError(`Field ${stmt.fieldName} in entity ${stmt.entityName} is a system field and cannot be marked as translatable.`);
            }
            if (!isRelation(field)) {
                it.translatable[stmt.entityName] = it.translatable[stmt.entityName] || [];
                if (!it.translatable[stmt.entityName]?.includes(stmt.fieldName)) {
                    it.translatable[stmt.entityName].push(stmt.fieldName);
                }
                return;
            }
            if (isInverseRelation(field)) {
                if (it.translatable[field.target]?.includes(field.ownedBy)) {
                    throw new SchemaDslBuilderError(`Field ${stmt.fieldName} in entity ${stmt.entityName} is translated by the inverse relation ${field.target}.${field.ownedBy}. You cannot mark both sides of a relation as translatable.`);
                }
            }
            if (isOwningRelation(field)) {
                if (field.inversedBy && it.translatable[field.target]?.includes(field.inversedBy)) {
                    throw new SchemaDslBuilderError(`Field ${stmt.fieldName} in entity ${stmt.entityName} is translated by the inverse relation ${field.target}.${field.inversedBy}. You cannot mark both sides of a relation as translatable.`);
                }
            }
            it.translatable[stmt.entityName] = it.translatable[stmt.entityName] || [];
            if (!it.translatable[stmt.entityName]?.includes(stmt.fieldName)) {
                it.translatable[stmt.entityName].push(stmt.fieldName);
            }
        });
    },
});
//# sourceMappingURL=SetTranslatableRelationStmt.js.map